import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTeamPenalties } from "@/hooks/useTeamPenalties";
// UPPDRAG-8 PR-E: dual-write reschedule system messages.
import { sendLeagueMatchMessage } from "@/lib/chatDualWrite";
import { Button } from "@/components/ui/button";
import { X, AlertTriangle } from "lucide-react";
import { format, addDays, addMinutes, startOfDay } from "date-fns";
import { sv } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { getLocalDayBounds } from "@/lib/utils";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useLeagueZones, getAvailableZoneSlots } from "@/hooks/useLeagueZones";
import { formatPrice } from "@/utils/formatPrice";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { buildAuditLogEntry } from "@/lib/rescheduleAuditHelpers";
import { useOrganization } from "@/hooks/useOrganization";

interface RescheduleRequestProps {
  matchId: string;
  onClose: () => void;
  /** PR-7.2: when set, this submission is a counter-proposal to an
   * existing request. The new row gets `parent_request_id = parentRequestId`,
   * the parent request is auto-rejected (status='rejected', responded_by
   * = current user), and the audit log records `action='counter_proposed'`
   * instead of `request_created`. */
  parentRequestId?: string | null;
}

function minutesToTime(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

export default function RescheduleRequest({ matchId, onClose, parentRequestId }: RescheduleRequestProps) {
  // PR-AV: useTranslation must be called at component scope (TS2304 fix).
  const { t } = useTranslation();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<{ time: string; courts: { id: string; name: string }[] } | null>(null);

  // 1. Fetch match → league config + team info
  const { data: match } = useQuery({
    queryKey: ["reschedule-match-info", matchId],
    queryFn: async () => {
      const { data } = await supabase
        .from("league_matches")
        .select("league_id, home_team_id, away_team_id, scheduled_date, scheduled_time, leagues(match_duration_minutes, hall_id, play_days, play_time_start, play_time_end, scoring_system, reschedule_deadline_hours, late_reschedule_fee, late_reschedule_fee_type, late_reschedule_fee_minor, late_reschedule_fee_percent, currency)")
        .eq("id", matchId)
        .single();
      return data;
    },
  });

  const league = (match as any)?.leagues;
  const hallId = league?.hall_id;
  const durationMin = league?.match_duration_minutes ?? 60;
  const scoringSystem = league?.scoring_system as any;
  const maxWoBeforeBlock = scoringSystem?.max_wo_before_block ?? 2;

  // Calculate late reschedule fee
  const lateFee = useMemo(() => {
    if (!match?.scheduled_date || !league) return 0;
    const scheduledDateTime = new Date(
      `${match.scheduled_date}T${match.scheduled_time ?? "00:00:00"}`
    );
    const hoursUntilMatch =
      (scheduledDateTime.getTime() - Date.now()) / (1000 * 60 * 60);
    const deadlineHours = league.reschedule_deadline_hours ?? 0;
    const feeType = league.late_reschedule_fee_type ?? "fixed";
    // Sprint D PR-AC Phase 4 split-aware: percent type reads discount_percent-style col,
    // fixed type reads _minor (öre → kr). Legacy late_reschedule_fee is fallback.
    const feeValue = feeType === "percent"
      ? ((league as any).late_reschedule_fee_percent ?? league.late_reschedule_fee ?? 0)
      : ((league as any).late_reschedule_fee_minor != null
          ? (league as any).late_reschedule_fee_minor / 100
          : (league.late_reschedule_fee ?? 0));

    if (hoursUntilMatch >= deadlineHours || feeValue <= 0) return 0;

    if (feeType === "percent") {
      // Estimate court cost based on duration (1h = base unit)
      const estimatedCost = 0; // no court cost available here
      return Math.round((estimatedCost * feeValue) / 100);
    }
    return feeValue;
  }, [match, league]);

  // Determine user's team
  // TASK-67 PR-D: collapsed the previous two-source union (teams.player1/2
  // + team_members.user_id) to team_members alone. PR-A's backfill ensured
  // every prior player1/2 user_id has a team_members row, so the membership
  // branch returns the same set the union previously did.
  const { data: userTeam } = useQuery({
    queryKey: ["user-team-for-match", matchId, user?.id],
    queryFn: async () => {
      if (!user || !match) return null;
      const matchTeamIds = [match.home_team_id, match.away_team_id];
      const membersRes = await supabase
        .from("team_members")
        .select("team_id")
        .eq("user_id", user.id)
        .in("team_id", matchTeamIds);
      const ids = new Set<string>();
      for (const tm of (membersRes.data ?? []) as { team_id: string | null }[]) {
        if (tm.team_id) ids.add(tm.team_id);
      }
      const first = ids.values().next().value;
      return first ? { id: first } : null;
    },
    enabled: !!user && !!match,
  });

  const { isBlocked } = useTeamPenalties(userTeam?.id, match?.league_id);

  // Fetch availability zones for the league
  const { zones } = useLeagueZones(match?.league_id);
  const hasZones = zones.length > 0;

  // 2. Date chips — next 14 eligible days
  const dates = useMemo(() => {
    const playDays: string[] = league?.play_days ?? [];
    const result: Date[] = [];

    // When zones exist, filter by zone day_of_week (ISO: 1=Mon..7=Sun)
    const zoneDays = hasZones
      ? [...new Set(zones.flatMap((z) => z.day_of_week))]
      : null;

    for (let i = 0; i < 28 && result.length < 14; i++) {
      const d = addDays(new Date(), i);
      if (zoneDays) {
        const jsDay = d.getDay();
        const isoDay = jsDay === 0 ? 7 : jsDay;
        if (zoneDays.includes(isoDay)) {
          result.push(startOfDay(d));
        }
      } else {
        const dayName = format(d, "EEEE", { locale: sv }).toLowerCase();
        if (playDays.length === 0 || playDays.includes(dayName)) {
          result.push(startOfDay(d));
        }
      }
    }
    return result;
  }, [league?.play_days, hasZones, zones]);

  // 3. Courts in the hall
  const { data: courts = [] } = useQuery({
    queryKey: ["reschedule-courts", hallId],
    queryFn: async () => {
      const { data } = await supabase
        .from("courts")
        .select("id, name")
        .eq("hall_id", hallId!)
        .eq("status", "active");
      return data ?? [];
    },
    enabled: !!hallId,
  });

  // 4. Bookings for the selected day
  const { data: dayBookings = [] } = useQuery({
    queryKey: ["reschedule-bookings", hallId, selectedDate?.toISOString()],
    queryFn: async () => {
      const { start, end } = getLocalDayBounds(selectedDate!);
      const { data } = await supabase
        .from("bookings")
        .select("court_id, start_time, end_time")
        .eq("status", "confirmed")
        .gte("start_time", start)
        .lte("start_time", end);
      return data ?? [];
    },
    enabled: !!hallId && !!selectedDate,
  });

  // 5. Available slots (zone-aware when zones exist, fallback to legacy)
  const availableSlots = useMemo(() => {
    if (!selectedDate || courts.length === 0) return [];

    if (hasZones) {
      return getAvailableZoneSlots(zones, selectedDate, durationMin, courts, dayBookings);
    }

    // Legacy fallback: use play_time_start / play_time_end
    const slots: { time: string; zoneName?: string; courts: { id: string; name: string }[] }[] = [];
    const startHour = league?.play_time_start ? parseInt(league.play_time_start) : 7;
    const endHour = league?.play_time_end ? parseInt(league.play_time_end) : 23;

    for (let min = startHour * 60; min + durationMin <= endHour * 60; min += 30) {
      const timeStr = minutesToTime(min);
      const slotStart = new Date(selectedDate);
      slotStart.setHours(Math.floor(min / 60), min % 60, 0, 0);
      const slotEnd = addMinutes(slotStart, durationMin);

      if (slotStart < new Date()) continue;

      const freeCourts = courts.filter((court: any) => {
        return !dayBookings.some(
          (b: any) =>
            b.court_id === court.id &&
            new Date(b.start_time) < slotEnd &&
            new Date(b.end_time) > slotStart
        );
      });

      if (freeCourts.length > 0) {
        slots.push({ time: timeStr, courts: freeCourts });
      }
    }
    return slots;
  }, [selectedDate, courts, dayBookings, durationMin, league, hasZones, zones]);

  // 6. Submit
  const mutation = useMutation({
    mutationFn: async () => {
      if (!selectedDate || !selectedSlot || !user) throw new Error("Välj datum och tid");

      const proposedDate = format(selectedDate, "yyyy-MM-dd");
      const courtId = selectedSlot.courts[0]?.id ?? null;
      const isCounterProposal = !!parentRequestId;

      // Sprint D PR-AC dual-write: lateFee is in kr, dual-write to fee_amount_minor (öre).
      // PR #19 trigger trg_dual_write_reschedule_requests_minor backfills if missing.
      // PR-7.2: include parent_request_id when this is a counter-proposal so the audit
      // log + future admin views can walk the chain.
      const insertPayload: Record<string, unknown> = {
        match_id: matchId,
        requested_by: user.id,
        proposed_date: proposedDate,
        proposed_time: selectedSlot.time + ":00",
        proposed_court_id: courtId,
        status: "pending",
        fee_amount: lateFee,
        fee_amount_minor: Math.round(lateFee * 100),
        currency: "SEK",
      };
      if (parentRequestId) insertPayload.parent_request_id = parentRequestId;
      const { data: insertedRequest, error: reqError } = await supabase
        .from("reschedule_requests" as never)
        .insert(insertPayload as never)
        .select("id")
        .single();
      if (reqError) throw reqError;
      const newRequestId = (insertedRequest as { id: string } | null)?.id;

      // PR-7.2: when counter-proposing, auto-reject the parent so only one
      // request stays "active" per match. Recorded as opponent_rejected
      // (current user nuked the prior proposal) — semantically equivalent
      // to "I'd rather use this time" from the audit log POV. The
      // counter_proposed entry on the new request links the chain.
      if (isCounterProposal) {
        await supabase
          .from("reschedule_requests" as never)
          .update({
            status: "rejected",
            responded_by: user.id,
            responded_at: new Date().toISOString(),
          } as never)
          .eq("id", parentRequestId);
      }

      // PR-7.2: audit-log entry. action='counter_proposed' when chained;
      // 'request_created' for fresh requests. Pure helper builds the
      // canonical payload — RLS WITH CHECK pins actor_id = auth.uid().
      if (newRequestId) {
        const auditEntry = buildAuditLogEntry({
          action: isCounterProposal ? "counter_proposed" : "request_created",
          requestId: newRequestId,
          actorId: user.id,
          actorRole: "player",
          parentRequestId: parentRequestId ?? null,
          metadata: {
            proposed_date: proposedDate,
            proposed_time: selectedSlot.time + ":00",
            proposed_court_id: courtId,
            fee_amount_minor: Math.round(lateFee * 100),
          },
        });
        await supabase
          .from("reschedule_audit_log" as never)
          .insert(auditEntry as never);
      }

      const displayDate = format(selectedDate, "d MMM", { locale: sv });
      const chatMessage = isCounterProposal
        ? `↩️ Motförslag: ${displayDate} kl ${selectedSlot.time}`
        : `📅 Ombokningsförslag: ${displayDate} kl ${selectedSlot.time}`;
      // UPPDRAG-8 PR-E: dual-write + structured metadata.
      await sendLeagueMatchMessage(supabase, {
        matchId,
        senderUserId: user.id,
        body: chatMessage,
        isSystem: true,
        data: {
          type: isCounterProposal ? "reschedule_counter_proposal" : "reschedule_proposal",
          proposed_date: format(selectedDate, "yyyy-MM-dd"),
          proposed_time: selectedSlot.time,
        },
      });

      // PR-7.2: notify opposing team's players. Roadmap §3.4 noted only
      // admin gets a notification on opponent-accept and the requesting
      // team gets nothing on admin-respond — but the OPPOSING team wasn't
      // notified at all when a request was first created (chat-only).
      // Wire it now so player B sees a bell ping when player A submits.
      if (organization) {
        try {
          const oppositeTeamIds = userTeam?.id === match?.home_team_id
            ? [match?.away_team_id]
            : userTeam?.id === match?.away_team_id
              ? [match?.home_team_id]
              : [match?.home_team_id, match?.away_team_id]; // fallback: notify both
          const validOppIds = oppositeTeamIds.filter(Boolean) as string[];
          if (validOppIds.length > 0) {
            // TASK-67 PR-D: roster pulled from team_members (role='primary')
            // instead of teams.player1/2_id. PR-A's backfill ensured every
            // prior player1/2 has a team_members row, so the result set is
            // identical. We notify all primaries (the playing roster) on
            // both opponent teams.
            const { data: oppMembers } = await supabase
              .from("team_members")
              .select("user_id")
              .in("team_id", validOppIds)
              .eq("role", "primary")
              .not("user_id", "is", null);
            const oppPlayerIds = [
              ...new Set(
                (oppMembers ?? []).map((m) => (m as { user_id: string | null }).user_id).filter(Boolean) as string[],
              ),
            ];
            if (oppPlayerIds.length > 0) {
              await supabase.from("notifications").insert(
                oppPlayerIds.map((uid) => ({
                  user_id: uid,
                  organization_id: organization.id,
                  type: isCounterProposal ? "reschedule_counter_proposed" : "reschedule_proposed",
                  title: isCounterProposal ? "Motförslag mottaget" : "Ombokningsförfrågan",
                  body: `${displayDate} kl ${selectedSlot.time}`,
                  data: { match_id: matchId, reschedule_id: newRequestId },
                })),
              );
            }
          }
        } catch {
          // Non-critical — chat message + audit row already landed.
        }
      }
    },
    onSuccess: () => {
      toast.success(t("activities:leagues.rescheduleProposalSent"));
      queryClient.invalidateQueries({ queryKey: ["reschedule-requests", matchId] });
      queryClient.invalidateQueries({ queryKey: ["match-chat", matchId] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(err.message || "Kunde inte skicka förslag");
    },
  });

  return (
    <div className="border rounded-lg p-3 space-y-3 bg-muted/50">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Föreslå ny tid</p>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {isBlocked(maxWoBeforeBlock) && (
        <div className="flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/30 p-3">
          <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-xs text-destructive">
            Ert lag har för många oförlåtna WO:er. Kontakta admin för att kunna omboka.
          </p>
        </div>
      )}

      {/* Date scroll */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
        {dates.map((d) => (
          <button
            key={d.toISOString()}
            className={cn(
              "flex flex-col items-center min-w-[3rem] px-2 py-1.5 rounded-lg text-xs transition-colors shrink-0",
              selectedDate?.toDateString() === d.toDateString()
                ? "bg-primary text-primary-foreground"
                : "hover:bg-muted"
            )}
            onClick={() => { setSelectedDate(d); setSelectedSlot(null); }}
          >
            <span className="capitalize">{format(d, "EEE", { locale: sv })}</span>
            <span className="font-semibold text-sm">{format(d, "d")}</span>
          </button>
        ))}
      </div>

      {/* Available slots */}
      {selectedDate && (
        availableSlots.length > 0 ? (
          <div className="space-y-2">
            {hasZones ? (
              // Group slots by zone name
              Object.entries(
                availableSlots.reduce<Record<string, typeof availableSlots>>((acc, slot) => {
                  const key = (slot as any).zoneName ?? "";
                  (acc[key] ??= []).push(slot);
                  return acc;
                }, {})
              ).map(([zoneName, slots]) => (
                <div key={zoneName}>
                  {zoneName && <p className="text-[10px] font-medium text-muted-foreground mb-1">{zoneName}</p>}
                  <div className="flex flex-wrap gap-1.5">
                    {slots.map((slot) => (
                      <button
                        key={`${zoneName}-${slot.time}`}
                        className={cn(
                          "px-3 py-1.5 rounded-md text-xs font-medium border transition-colors",
                          selectedSlot?.time === slot.time
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border hover:border-primary/50"
                        )}
                        onClick={() => setSelectedSlot(slot)}
                      >
                        {slot.time}
                        <span className="text-[9px] text-muted-foreground ml-1">
                          ({slot.courts.length} {slot.courts.length === 1 ? "bana" : "banor"})
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {availableSlots.map((slot) => (
                  <button
                    key={slot.time}
                    className={cn(
                      "px-3 py-1.5 rounded-md text-xs font-medium border transition-colors",
                      selectedSlot?.time === slot.time
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border hover:border-primary/50"
                    )}
                    onClick={() => setSelectedSlot(slot)}
                  >
                    {slot.time}
                    <span className="text-[9px] text-muted-foreground ml-1">
                      ({slot.courts.length} {slot.courts.length === 1 ? "bana" : "banor"})
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-3">
            Inga lediga tider denna dag
          </p>
        )
      )}

      {lateFee > 0 && (
        <Alert variant="destructive" className="py-2">
          <AlertDescription className="text-xs">
            ⚠️ Ombokning efter deadline ({league?.reschedule_deadline_hours}h). Avgift: {formatPrice(lateFee)}
          </AlertDescription>
        </Alert>
      )}

      <Button
        size="sm"
        className="w-full"
        disabled={!selectedSlot || mutation.isPending || isBlocked(maxWoBeforeBlock)}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending
          ? "Skickar..."
          : lateFee > 0
            ? `Föreslå ombokning (${formatPrice(lateFee)})`
            : "Föreslå ombokning"}
      </Button>
    </div>
  );
}
